<template>
    <div 
        v-if="visible" 
        class="file-context-menu"
        :style="{ top: position.y + 'px', left: position.x + 'px' }"
        @click.stop
        ref="menu"
    >
        <div class="context-menu-item" @click="handleAction('view')" v-if="canView">
            <i class="fa fa-eye"></i>
            <span>{{ $t('view') }}</span>
        </div>
        
        <div class="context-menu-item" @click="handleAction('download')" v-if="canDownload">
            <i class="fa fa-download"></i>
            <span>{{ $t('download') }}</span>
        </div>
        
        <div class="context-menu-item" @click="handleAction('share')" v-if="canShare">
            <i class="fa fa-share-alt"></i>
            <span>{{ $t('share') }}</span>
        </div>
        
        <div class="context-menu-item" @click="handleAction('copyUrl')" v-if="canCopyUrl">
            <i class="fa fa-link"></i>
            <span>{{ $t('copyUrl') }}</span>
        </div>
        
        <div class="context-menu-item" @click="handleAction('info')" v-if="canShowInfo">
            <i class="fa fa-info-circle"></i>
            <span>{{ $t('info') }}</span>
        </div>
        
        <div class="context-menu-item" @click="handleAction('move')" v-if="canMove">
            <i class="fa fa-arrows"></i>
            <span>{{ $t('move') }}</span>
        </div>
        
        <!-- Tools Submenu -->
        <div 
            class="context-menu-item submenu-parent" 
            @mouseenter="showToolsMenu"
            @mouseleave="hideToolsSubmenu"
            v-if="hasTools"
        >
            <i class="fa fa-wrench"></i>
            <span>{{ $t('tools') }}</span>
            <i class="fa fa-chevron-right submenu-arrow"></i>
            
            <!-- Tools Submenu -->
            <div 
                v-if="showToolsSubmenu" 
                class="context-submenu"
                @mouseenter="showToolsMenu"
                @mouseleave="hideToolsSubmenu"
            >
                <!-- PDF Tools -->
                <template v-if="isPdf">
                    <div class="context-menu-item" @click="handleAction('pdfToHtml')">
                        <i class="fa fa-file-code-o"></i>
                        <span>{{ $t('convertPdfToHtml') }}</span>
                    </div>
                    <div class="context-menu-item" @click="handleAction('pdfFormFields')">
                        <i class="fa fa-wpforms"></i>
                        <span>Form Fields Info</span>
                    </div>
                </template>
                
                <!-- HTML Tools -->
                <template v-if="isHtml">
                    <div class="context-menu-item" @click="handleAction('editHtmlWysiwyg')">
                        <i class="fa fa-edit"></i>
                        <span>{{ $t('editWithWysiwyg') }}</span>
                    </div>
                    <div class="context-menu-item" @click="handleAction('htmlToPdoc')">
                        <i class="fa fa-file-text-o"></i>
                        <span>{{ $t('convertHtmlToPdoc') }}</span>
                    </div>
                </template>

                <!-- Text Tools -->
                <template v-if="isTxt">
                    <div class="context-menu-item" @click="handleAction('txtToPdf')">
                        <i class="fa fa-file-pdf-o"></i>
                        <span>{{ $t('convertTxtToPdf') }}</span>
                    </div>
                </template>

                <!-- Markdown Tools -->
                <template v-if="isMd">
                    <div class="context-menu-item" @click="handleAction('editMdFile')">
                        <i class="fa fa-edit"></i>
                        <span>{{ $t('editMarkdownFile') }}</span>
                    </div>
                </template>
                
                <!-- Extract Text (documents, spreadsheets, presentations, etc.) -->
                <template v-if="canExtractText">
                    <div class="context-menu-item" @click="handleAction('extractText')">
                        <i class="fa fa-file-text-o"></i>
                        <span>{{ $t('extractText') }}</span>
                    </div>
                </template>

                <!-- Image Tools -->
                <template v-if="isImage">
                    <div class="context-menu-item" @click="handleAction('imageExif')">
                        <i class="fa fa-info"></i>
                        <span>EXIF Data</span>
                    </div>
                </template>
            </div>
        </div>
        
        <div class="context-menu-divider" v-if="canDelete"></div>
        
        <div class="context-menu-item danger" @click="handleAction('delete')" v-if="canDelete">
            <i class="fa fa-trash"></i>
            <span>{{ $t('delete') }}</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FileContextMenu',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        position: {
            type: Object,
            default: () => ({ x: 0, y: 0 })
        },
        file: {
            type: Object,
            required: true
        },
        isPrivate: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showToolsSubmenu: false,
            submenuTimeout: null
        };
    },
    computed: {
        fileExtension() {
            if (!this.file || !this.file.name) return '';
            const parts = this.file.name.split('.');
            return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
        },
        isPdf() {
            return this.fileExtension === 'pdf';
        },
        isHtml() {
            return this.fileExtension === 'html' || this.fileExtension === 'htm';
        },
        isImage() {
            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif'];
            return imageExtensions.includes(this.fileExtension);
        },
        isTxt() {
            return this.fileExtension === 'txt';
        },
        isMd() {
            return this.fileExtension === 'md';
        },
        canExtractText() {
            const extractable = [
                'pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt',
                'odt', 'ods', 'odp', 'rtf', 'epub',
                'csv', 'tsv', 'xml', 'json',
                'eml', 'msg',
                'dbf',
                'jpg', 'jpeg', 'png', 'webp', 'tiff', 'tif', 'bmp'
            ];
            return extractable.includes(this.fileExtension);
        },
        hasTools() {
            return this.isPdf || this.isHtml || this.isTxt || this.isImage || this.isMd || this.canExtractText;
        },
        canView() {
            return true;
        },
        canDownload() {
            return this.$can('download protokol-media');
        },
        canShare() {
            return this.isPrivate;
        },
        canCopyUrl() {
            return !this.isPrivate;
        },
        canShowInfo() {
            return true;
        },
        canMove() {
            return this.$can('edit protokol-media');
        },
        canDelete() {
            return this.$can('delete protokol-media');
        }
    },
    methods: {
        handleAction(action) {
            this.$emit('action', action);
        },
        showToolsMenu() {
            if (this.submenuTimeout) {
                clearTimeout(this.submenuTimeout);
            }
            this.showToolsSubmenu = true;
        },
        hideToolsSubmenu() {
            if (this.submenuTimeout) {
                clearTimeout(this.submenuTimeout);
            }
            this.submenuTimeout = setTimeout(() => {
                this.showToolsSubmenu = false;
            }, 300);
        }
    },
    beforeDestroy() {
        if (this.submenuTimeout) {
            clearTimeout(this.submenuTimeout);
        }
    },
    mounted() {
        // Adjust menu position if it goes off-screen
        this.$nextTick(() => {
            if (this.$refs.menu) {
                const menu = this.$refs.menu;
                const rect = menu.getBoundingClientRect();
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                
                let newX = this.position.x;
                let newY = this.position.y;
                
                if (rect.right > viewportWidth) {
                    newX = viewportWidth - rect.width - 10;
                }
                
                if (rect.bottom > viewportHeight) {
                    newY = viewportHeight - rect.height - 10;
                }
                
                if (newX !== this.position.x || newY !== this.position.y) {
                    this.$emit('update:position', { x: newX, y: newY });
                }
            }
        });
    }
}
</script>

<style scoped>
.file-context-menu {
    position: fixed;
    background: var(--form-bg);
    border: 1px solid var(--border-default);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 10000;
    min-width: 180px;
    padding: 4px 0;
    color: var(--text-primary);
}

.context-menu-item {
    padding: 10px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    transition: background-color 0.2s ease;
    position: relative;
}

.context-menu-item:hover {
    background-color: var(--bg-card);
}

.context-menu-item.danger {
    color: #ef4444;
}

.context-menu-item.danger:hover {
    background-color: rgba(239, 68, 68, 0.1);
}

.context-menu-item i {
    width: 16px;
    text-align: center;
    font-size: 14px;
}

.context-menu-divider {
    height: 1px;
    background-color: var(--border-default);
    margin: 4px 0;
}

.submenu-parent {
    position: relative;
}

.submenu-arrow {
    margin-left: auto;
    font-size: 12px;
}

.context-submenu {
    position: absolute;
    left: 100%;
    top: -4px;
    background: var(--form-bg);
    border: 1px solid var(--border-default);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    min-width: 180px;
    padding: 4px 0;
    margin-left: -1px;
}

.context-submenu .context-menu-item {
    white-space: nowrap;
}
</style>
